import { Box, Theme } from "@outschool/backpack";
import { ApolloError } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import {
  CategoryNavbar,
  ContentfulAnnouncementBanner,
  ContentfulAnnouncementBannerItem,
  PageAlerts,
  ReadOnlyBanner,
  useContentfulAnnouncementBannerHook,
} from "@outschool/ui-components-website";
import React, { useEffect, useState } from "react";

import * as Env from "../../../shared/Env";
import SectionStartReminder from "../../containers/SectionStartReminder";
import { StickyHeaderProvider } from "../../hooks/useStickyHeader";
import { UserMode, UserModeProvider, useUserMode } from "../../lib/UserMode";
import AccountBanner from "../AccountBanner";
import { AiSummaryBanner } from "../banners/AiSummaryBanner";
import MembershipsBannerSwitcher from "../banners/MembershipsBannerSwitcher";
import SelfIdentificationPublicFunding from "../banners/SelfIdentificationPublicFunding";
import Footer from "../Footer";
import { GiftCardModal } from "../giftCards/GiftCardModal";
import Header from "../nav/Header";
import { MobileFooterTabBar } from "../nav/MobileFooterTabBar";
import ReviewReminderTopNoticeBar from "../ReviewReminderTopNoticeBar";
import SignupModal from "../SignupModal";
import TimedTopNoticeBar from "../TimedTopNoticeBar";
import TopNoticeBar from "../TopNoticeBar";
import StickyHeaderConatiner from "./StickyHeaderContainer";

export type SimpleLayoutProps = {
  children?: React.ReactNode;
  error?: string | ApolloError;
  narrowColumn?: boolean;
  narrowPaddedColumn?: boolean;
  grayBackground?: boolean;
  hideFooter?: boolean;
  hideReviewReminder?: boolean;
  hideAccountBanner?: boolean;
  sx?: React.CSSProperties;
  contentSx?: React.CSSProperties;
  userMode?: UserMode;
  allowCategoriesNavbar?: boolean;
  hideSignUpModal?: boolean;
  hideMobileFooterTabBar?: boolean;
  showSearchBox?: boolean;
  hideHeaderRight?: boolean;
  customHeader?: React.ReactNode;
};

const useCategoriesNavbar = ({ enabled }: { enabled: boolean }) => {
  const userMode = useUserMode();
  const { isLoggedIn } = useSession();

  const shouldShowCategories = enabled && userMode !== UserMode.Educator;

  return {
    showInHeader: shouldShowCategories && !isLoggedIn,
    showAsNavbar: shouldShowCategories && isLoggedIn,
  };
};

const SimpleLayout = React.forwardRef<HTMLDivElement, SimpleLayoutProps>(
  (
    {
      children,
      error,
      narrowColumn,
      narrowPaddedColumn,
      grayBackground,
      customHeader,
      hideFooter = false,
      hideReviewReminder = false,
      hideAccountBanner = false,
      sx = {},
      contentSx = {},
      userMode = null,
      allowCategoriesNavbar = false,
      hideSignUpModal = false,
      hideMobileFooterTabBar = false,
      showSearchBox = true,
      hideHeaderRight = false,
    },
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const [contentfulBannerData, setContentfulBannerData] =
      useState<ContentfulAnnouncementBannerItem>(null);
    const bannerData = useContentfulAnnouncementBannerHook();
    useEffect(() => {
      setContentfulBannerData(bannerData as ContentfulAnnouncementBannerItem);
    }, [bannerData]);
    const mobileFooterRef = React.useRef<HTMLElement>(null);
    const bottomOffset =
      mobileFooterRef.current?.getBoundingClientRect().height ?? 0;
    const { showInHeader, showAsNavbar } = useCategoriesNavbar({
      enabled: allowCategoriesNavbar && !customHeader,
    });

    const headerRef = React.useRef<HTMLDivElement>(null);

    return (
      <UserModeProvider value={userMode}>
        <StickyHeaderProvider headerRef={headerRef}>
          <MembershipsBannerSwitcher />
          <StickyHeaderConatiner ref={headerRef}>
            {!!customHeader ? (
              customHeader
            ) : (
              <Header
                showCategoriesNavbar={showInHeader}
                showSearchBox={showSearchBox}
                hideHeaderRight={hideHeaderRight}
              />
            )}
          </StickyHeaderConatiner>
          {showAsNavbar && (
            <CategoryNavbar
              sx={(theme: Theme) => ({
                position: "relative",
                backgroundColor: "common.white",
                [theme.breakpoints.down("md")]: {
                  borderTop: "1px solid",
                  borderColor: theme.palette.grey[200],
                },
              })}
            />
          )}
          <Box>
            <TopNoticeBar topNotice={error} />
            <TimedTopNoticeBar />
            {Env.IS_READ_ONLY_MODE && <ReadOnlyBanner />}

            <ContentfulAnnouncementBanner bannerData={contentfulBannerData} />
            {!hideAccountBanner && <AccountBanner />}
            <SectionStartReminder />
            {!hideReviewReminder && <ReviewReminderTopNoticeBar />}
            <AiSummaryBanner />
            <PageAlerts />
          </Box>

          <Box
            sx={[
              {
                minHeight: "100%",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                ...((grayBackground || narrowColumn || narrowPaddedColumn) && {
                  backGroundColor: "#FAFAFA",
                }),
                marginBottom: bottomOffset,
              },
              ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            ref={ref}
          >
            <Box
              sx={(theme: Theme) => ({
                flexGrow: 1,
                ...(narrowColumn && {
                  maxWidth: "750px",
                  position: "relative",
                  width: "100%",
                  marginLeft: "auto",
                  marginRight: "auto",
                  boxSizing: "border-box",
                  backgroundColor: "#FFF",
                }),
                ...(narrowPaddedColumn && {
                  maxWidth: "750px",
                  position: "relative",
                  width: "100%",
                  marginLeft: "auto",
                  marginRight: "auto",
                  boxSizing: "border-box",
                  backgroundColor: "#FFF",
                  paddingY: "2em",
                  paddingX: "1em",
                  [theme.breakpoints.up("md")]: {
                    paddingX: "2em",
                  },
                }),
                ...contentSx,
              })}
            >
              {children}
            </Box>
            <SelfIdentificationPublicFunding />
            {!hideMobileFooterTabBar && (
              <MobileFooterTabBar ref={mobileFooterRef} />
            )}
            {!hideFooter && <Footer />}
            {!hideSignUpModal && <SignupModal />}
            <GiftCardModal />
          </Box>
        </StickyHeaderProvider>
      </UserModeProvider>
    );
  },
);

export default SimpleLayout;
